import {useState, useEffect} from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import { MdSettings as SettingsIcon, MdPerson as PersonIcon } from 'react-icons/md';
import ProfileView from '@CommonViews/ProfileView';
import PermissionsView from '@CommonViews/PermissionsView';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import Tooltip from '@mui/material/Tooltip';
import './UserListRow.scss';

export default function UserListRow(props) {

    const [cursor, setCursor] = useState('auto');
    const [permissions, setPermissions] = useState([]);
    const [activeUserPermissions, setActiveUserPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [showProfileView, setShowProfileView] = useState(false);
    const [showPermissionsView, setShowPermissionsView] = useState(false);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const userAuthService = new UserAuthService();
    const errorHandler = new ErrorHandler();
    const { activeUserId } = useReduxActiveUser();
    const className = 'userListRow';

    useEffect(() => {
        renderData();
    }, [props])

    const renderData = async () => {
        if (!props.user?.id) {
            return;
        }
        setPermissionsLoading(true);
        try {
            let userPermissions, currentUserPermission;
            if(!!props.project) {
                userPermissions = await userAuthService.getUserPermissionForParent(props.user?.id, 'project', props.project?.id);
                currentUserPermission = await userAuthService.getUserPermissionForParent(activeUserId, 'project', props.project?.id);
            } else if(!!props.org) {
                userPermissions = await userAuthService.getUserPermissionForParent(props.user?.id, 'org', props.org?.id);
                currentUserPermission = await userAuthService.getUserPermissionForParent(activeUserId, 'org', props.org?.id);
            } else if(!!props.team) {
                userPermissions = await userAuthService.getUserPermissionForParent(props.user?.id, 'team', props.team?.id);
                currentUserPermission = await userAuthService.getUserPermissionForParent(activeUserId, 'team', props.team?.id);
            }
            userPermissions = JSON.parse(userPermissions.body);
            currentUserPermission = JSON.parse(currentUserPermission.body);
            setPermissions(userPermissions);
            setActiveUserPermissions(currentUserPermission)
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setPermissionsLoading(false);
        }
    }

    const toggleProfileView = () => {
        setShowProfileView(!showProfileView);
    }

    const togglePermissionsView = () => {
        setShowPermissionsView(!showPermissionsView);
    }

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const isOwner = props.project?.ownerUserId === props.user?.id || 
                    props.org?.ownerUserId === props.user?.id || 
                    props.team?.ownerUserId === props.user?.id;

    if (!props.user) {
        return null;
    }

    return (
        <div className={className}>
            <div className={`${className}__left`}>
                <div className={`${className}__avatar`}>
                    <PersonIcon />
                </div>
                <div className={`${className}__info`}>
                    <div className={`${className}__name`}>{props.user.fullName}</div>
                    <div className={`${className}__username`} onClick={toggleProfileView}>
                        @{props.user.userName}
                    </div>
                </div>
            </div>
            <div className={`${className}__right`}>
                <div className={`${className}__permissions`}>
                    {permissionsLoading ? (
                        <Skeleton variant="rectangular" width={80} height={28} sx={{borderRadius: '6px'}} />
                    ) : isOwner ? (
                        <span className={`${className}__badge owner`}>Owner</span>
                    ) : permissions.allowAdmin ? (
                        <span className={`${className}__badge admin`}>Admin</span>
                    ) : permissions.allowView ? (
                        <>
                            <span className={`${className}__badge view`}>View</span>
                            {permissions.allowCreate && (
                                <span className={`${className}__badge create`}>Create</span>
                            )}
                        </>
                    ) : null}
                </div>
                {!permissionsLoading && activeUserPermissions.allowAdmin && (
                    <>
                        <Tooltip title="Manage Permissions" arrow>
                            <button className={`${className}__settings`} onClick={togglePermissionsView}>
                                <SettingsIcon />
                            </button>
                        </Tooltip>
                    </>
                )}
            </div>
            {showProfileView && (
                <ProfileView
                    open={showProfileView}
                    closePopup={toggleProfileView}
                    profileId={props.user.id}/>
            )}
            {showPermissionsView && (
                <PermissionsView
                    open={showPermissionsView}
                    closePopup={togglePermissionsView}
                    user={props.user}
                    permissions={permissions}
                    activeUserPermissions={activeUserPermissions}
                    project={props.project}
                    org={props.org}
                    team={props.team}
                    parentType={props.parentType}
                    type='user'
                    reloadData={props.reloadData}
                    setSnackBarMessage={setSnackBarMessage}
                    setSnackBarOpen={setSnackBarOpen}
                    setSnackBarType={setSnackBarType}/>
            )}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}>
                <Alert
                    onClose={handleSnackBarClose}
                    severity={snackBarType}
                    variant='filled'
                    sx={{ width: '100%' }}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
